/**
 * Nathalie Robert — event-based follow-up messages.
 *
 * Triggered by specific player actions (opening a new depot, finalising
 * an M&A, hiring a senior driver, etc.). The message lands in the inbox
 * THE FOLLOWING DAY through the `deferredMessages` queue.
 *
 * Each builder returns a Messagerie payload OR null when Nathalie isn't
 * hired (the message is simply not enqueued).
 *
 * 31/05/2026 — Nathalie reactive layer (event-driven, in addition to
 * weekly recap & monthly advice).
 */

const TTL_DAYS = 30;

const isHired = (state) =>
  !!(state && state.hiredSupport && state.hiredSupport['role-nathalie-robert']);

/* ────────────────────────────────────────────────────────────────────
 * Event : a new depot was opened (independent or HQ #2/#3).
 * ──────────────────────────────────────────────────────────────────── */
export function buildDepotOpenedFollowup(state, depot) {
  if (!isHired(state) || !depot) return null;
  return {
    senderType: 'support',
    senderName: 'Nathalie Robert (Assistante)',
    senderRole: 'role-nathalie-robert',
    tone: 'friendly',
    kind: 'info',
    subject: `Ouverture de ${depot.name} — démarches en cours`,
    body:
      `Bonjour Patron,\n\n` +
      `Vous avez signé hier pour **${depot.name}**. Petit point administratif :\n\n` +
      `• **Déclaration DREAL** : transmise ce matin (capacité véhicules, périmètre d'exploitation).\n` +
      `• **Contrats utilities** : eau, électricité et internet — fournisseurs locaux contactés, mise en service estimée sous 5 jours ouvrés.\n` +
      `• **Affichage obligatoire** : règlement intérieur + consignes sécurité installés au panneau du dépôt.\n` +
      `• **Embauche locale** : si vous souhaitez recruter un chauffeur du secteur, j'ai 2 profils via Karim — à valider quand vous voudrez.\n\n` +
      `Tout sera prêt pour la mise en service. Bonne ouverture !\n\n` +
      `— Nathalie`,
    ttlDays: TTL_DAYS,
    receivedDay: state.totalDays || 0,
  };
}

/* ────────────────────────────────────────────────────────────────────
 * Event : an M&A acquisition was finalised (closing signed).
 * ──────────────────────────────────────────────────────────────────── */
export function buildAcquisitionFollowup(state, summary) {
  if (!isHired(state) || !summary) return null;
  const driversInherited = summary.driversInherited || 0;
  const vehiclesInherited = summary.vehiclesInherited || 0;
  const competitorName = summary.competitorName || 'la cible';
  return {
    senderType: 'support',
    senderName: 'Nathalie Robert (Assistante)',
    senderRole: 'role-nathalie-robert',
    tone: 'friendly',
    kind: 'info',
    subject: `Intégration ${competitorName} — checklist 100 jours`,
    body:
      `Bonjour Patron,\n\n` +
      `Closing **${competitorName}** validé hier. Pour les premiers jours :\n\n` +
      `• **Reprise du personnel** : ${driversInherited} chauffeur${driversInherited > 1 ? 's' : ''} inscrit${driversInherited > 1 ? 's' : ''} au registre, contrats de travail harmonisés sur votre convention.\n` +
      `• **Cartes carburant + badges péage** : commandées pour les ${vehiclesInherited} véhicule${vehiclesInherited > 1 ? 's' : ''} hérité${vehiclesInherited > 1 ? 's' : ''} (livraison 7 j).\n` +
      `• **Migration IT** : les emails @${competitorName.toLowerCase().replace(/\s+/g, '')}.fr sont redirigés vers vos boîtes pendant 90 j.\n` +
      `• **Communication clients** : courrier d'information préparé, signature attendue de votre part avant envoi.\n` +
      `• **Plan 100 j** : team-building J+30, badges/uniformes unifiés J+60 (cf. votre manuel pédagogique chapitre M&A).\n\n` +
      `On garde le cap.\n\n` +
      `— Nathalie`,
    ttlDays: TTL_DAYS,
    receivedDay: state.totalDays || 0,
  };
}

/* ────────────────────────────────────────────────────────────────────
 * Event : a senior driver (level ≥ 5) was just hired.
 * ──────────────────────────────────────────────────────────────────── */
export function buildSeniorHireFollowup(state, driver) {
  if (!isHired(state) || !driver) return null;
  const specsLine = (driver.specialties || []).length > 0
    ? `Spécialités : ${(driver.specialties || []).join(', ')}.`
    : 'Aucune spécialité particulière encore.';
  return {
    senderType: 'support',
    senderName: 'Nathalie Robert (Assistante)',
    senderRole: 'role-nathalie-robert',
    tone: 'friendly',
    kind: 'info',
    subject: `Arrivée de ${driver.name} — onboarding`,
    body:
      `Bonjour Patron,\n\n` +
      `${driver.name} a démarré aujourd'hui. C'est un chauffeur **senior** (niveau ${driver.level || 5}), bonne pioche.\n\n` +
      `• **Dossier administratif** : carte grise, permis ${(driver.licenses || []).join('/')} et chronotachygraphe vérifiés.\n` +
      `• ${specsLine}\n` +
      `• **Petit conseil** : un senior aime être consulté. Un échange de 5 minutes ce mois-ci sur son affectation préférée vaut mieux que le mettre devant le fait accompli.\n\n` +
      `Bienvenue dans l'équipe !\n\n` +
      `— Nathalie`,
    ttlDays: TTL_DAYS,
    receivedDay: state.totalDays || 0,
  };
}
